import {
  Box,
  FormControlLabel,
  MenuItem,
  Select,
  Stack,
  Switch,
} from '@mui/material'
import { RuleDefTypeEnum } from '@ulysses-inc/harami_api_client'
import type { FormikProps } from 'formik'
import type { ChangeEvent, FC } from 'react'
import { shallowEqual, useSelector } from 'react-redux'
import FormikErrorMessage from 'src/components/formikErrorMessage/FormikErrorMessage'
import type { RootState } from 'src/state/store'
import type { NumberRuleProps } from './EditResponseNumberRuleEnableProductMasterDrawer'
import { FixedRuleSelector } from './FixedRuleSelector'
import { MasterRuleSelector } from './MasterRuleSelector'

type Props = {
  formikProps: FormikProps<NumberRuleProps>
}

const NormalValueRow: FC<Props> = ({ formikProps }) => {
  const {
    values: { hasNumberRule, ruleDefType },
    setFieldValue,
    errors,
  } = formikProps
  const update = (key: keyof NumberRuleProps) => (value: unknown) =>
    setFieldValue(key, value)

  const onChange = (_: ChangeEvent<HTMLInputElement>, checked: boolean) => {
    if (!checked) {
      update('minimum')(undefined)
      update('maximum')(undefined)
      update('scale')(undefined)
      update('subType')(undefined)
      update('ruleDefType')(undefined)
      update('minimumRuleVariableDef')(undefined)
      update('maximumRuleVariableDef')(undefined)
    }
    update('hasNumberRule')(checked)
  }

  // 製品マスタが選択されていないと「製品マスタから設定する」は無効
  const masterUuid = useSelector(
    (state: RootState) => state.templatesState.editTemplate.masterUuid,
    shallowEqual,
  )
  const isMasterOptionDisabled = !masterUuid

  return (
    <Stack spacing={1}>
      <FormControlLabel
        control={<Switch checked={hasNumberRule} onChange={onChange} />}
        label="正常値を設定する"
      />
      {hasNumberRule && (
        <Stack spacing={1}>
          <Select<RuleDefTypeEnum | ''>
            displayEmpty
            size="small"
            value={ruleDefType ?? ''}
            onChange={e => update('ruleDefType')(e.target.value || undefined)}
            inputProps={{ 'aria-label': '設定方法の選択' }}
            renderValue={value => {
              if (value === '') {
                return (
                  <Box component="span" sx={{ color: 'text.disabled' }}>
                    設定方法を選択
                  </Box>
                )
              }
              return value === RuleDefTypeEnum.Fixed
                ? '数値で設定する'
                : '製品マスタから設定する'
            }}
            sx={{ width: 232 }}
          >
            <MenuItem
              value={RuleDefTypeEnum.Fixed}
              data-testid="select-number-ruledeftype-item"
            >
              数値で設定する
            </MenuItem>
            <MenuItem
              value={RuleDefTypeEnum.Master}
              disabled={isMasterOptionDisabled}
              data-testid="select-number-ruledeftype-item"
              sx={{ width: '100%' }}
            >
              {isMasterOptionDisabled
                ? '製品マスタを指定すると、項目を参照できます'
                : '製品マスタから設定する'}
            </MenuItem>
          </Select>
          {ruleDefType === RuleDefTypeEnum.Fixed && (
            <FixedRuleSelector formikProps={formikProps} />
          )}
          {ruleDefType === RuleDefTypeEnum.Master && (
            <MasterRuleSelector formikProps={formikProps} />
          )}
        </Stack>
      )}
      <Stack>
        <FormikErrorMessage name="subType" errors={errors} />
        <FormikErrorMessage name="maximum" errors={errors} />
        <FormikErrorMessage name="minimum" errors={errors} />
        <FormikErrorMessage name="ruleDefType" errors={errors} />
        <FormikErrorMessage name="minimumRuleVariableDef" errors={errors} />
        <FormikErrorMessage name="maximumRuleVariableDef" errors={errors} />
      </Stack>
    </Stack>
  )
}

export default NormalValueRow
